/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	IContributionCyclePayload,
	IContributionPayload,
} from "../../utility/types/contributionType";
import {
	submitCreateContribution,
	submitEndCycle,
	submitFetchContributionCycleById,
	submitGetContributionChartData,
	submitGetContributionStatus,
	submitGetCurrentCycleById,
	submitGetCurrentCycleOfCircle,
	submitGetMembersContributionStatusOfCircleCycle,
	submitStartCycle,
	submitPayMissedContributions,
	IMissedContributionPayload,
} from "../services/contributionService";

export const createContribution = createAsyncThunk(
	"contribution/member-contribution",
	async (
		{ payload }: { payload: IContributionPayload },
		{ rejectWithValue }
	) => {
		try {
			const response = await submitCreateContribution(payload);

			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
					error.message ||
					"Unknown Error Occurred"
			);
		}
	}
);

export const payMissedContributions = createAsyncThunk(
	"contribution/pay-missed-contributions",
	async (
		{ payload }: { payload: IMissedContributionPayload },
		{ rejectWithValue }
	) => {
		try {
			const response = await submitPayMissedContributions(payload);

			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
					error.message ||
					"Unknown Error Occurred"
			);
		}
	}
);

export const getContributionStatus = createAsyncThunk(
	"contribution/member-contribution-status",
	async (
		{ unionId, memberId }: { unionId: string; memberId: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await submitGetContributionStatus(unionId, memberId);

			return { unionId, memberId, ...response };
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
					error.message ||
					"Unknown Error Occurred"
			);
		}
	}
);

export const startCycle = createAsyncThunk(
	"contribution/start-cycle",
	async (
		{
			unionId,
			payload,
		}: { unionId: string; payload: IContributionCyclePayload },
		{ rejectWithValue }
	) => {
		try {
			const response = await submitStartCycle(unionId, payload);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
					error.message ||
					"Unknown Error Occurred"
			);
		}
	}
);

export const endCycle = createAsyncThunk(
	"contribution/end-cycle",
	async (
		{ cycleId, unionId }: { cycleId: string; unionId: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await submitEndCycle(cycleId, unionId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
					error.message ||
					"Unknown Error Occurred"
			);
		}
	}
);

export const getCurrentCycleOfCircle = createAsyncThunk(
	"contribution/cycle",
	async (
		{ unionId }: { unionId: string },
		{ rejectWithValue, fulfillWithValue }
	) => {
		try {
			const response = await submitGetCurrentCycleOfCircle(unionId);
			return response;
		} catch (error: any) {
			const msg = error.response?.data?.message;
			if (msg === "No active contribution cycle found for the current month") {
				// turn that “expected” 400 into a fulfilled null
				return fulfillWithValue(null);
			}
			// everything else is a real error
			return rejectWithValue(msg || error.message || "Unknown Error Occurred");
		}
	}
);

export const getCurrentCycleById = createAsyncThunk(
	"contribution/cycle-by-id",
	async (
		{ unionId, cycleId }: { unionId: string; cycleId: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await submitGetCurrentCycleById(unionId, cycleId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
					error.message ||
					"Unknown Error Occurred"
			);
		}
	}
);

export const getMembersContributionStatusOfCircleCycle = createAsyncThunk(
	"contribution/members-contribution-status",
	async (
		{ unionId, cycleId }: { unionId: string; cycleId: string },
		{ rejectWithValue }
	) => {
		try {
			const response = await submitGetMembersContributionStatusOfCircleCycle(
				unionId,
				cycleId
			);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
					error.message ||
					"Unknown Error Occurred"
			);
		}
	}
);

export const getContributionCycleById = createAsyncThunk(
	"cycleById",
	async ({ cycleId }: { cycleId: string }, { rejectWithValue }) => {
		try {
			const response = await submitFetchContributionCycleById(cycleId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data.message ||
					error.message ||
					"Unknown Error Occurred"
			);
		}
	}
);

export const updateContributionChartRealTime = createAsyncThunk(
	"contribution/chart-real-time-update",
	async (
		{
			unionId,
			contributionStats,
			newContribution,
		}: {
			unionId: string;
			contributionStats: any;
			newContribution: any;
		},
		{ rejectWithValue }
	) => {
		try {
			return {
				unionId,
				contributionStats,
				newContribution,
				timeStamp: new Date().toISOString(),
			};
		} catch (error: any) {
			return rejectWithValue(error.message || "Failed to update chart data");
		}
	}
);

export const getContributionChartData = createAsyncThunk(
	"contribution/chart-data",
	async (
		{
			unionId,
			period,
			cycleId,
		}: {
			unionId: string;
			period?: "today" | "month" | "year";
			cycleId?: string; // Make optional
		},
		{ rejectWithValue }
	) => {
		try {
			const params = new URLSearchParams();

			if (period) params.append("period", period);
			if (cycleId) params.append("cycleId", cycleId);

			const response = await submitGetContributionChartData(unionId, params);

			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data?.message ||
					error.message ||
					"Failed to fetch contribution chart data"
			);
		}
	}
);
